import type { FieldSpecification } from "@bridge/integration-core";
import type { AccountDto } from "@bridge/server/src/api/accounts/dto/account.dto";

export type AccountSelectionMode = "single" | "multi" | "all";

export enum Steps {
  Init,
  InstitutionSelect,
  Permissions,
  Authentication,
  AccountSelect,
  Finished,
}

export interface AppPermission {
  enabled: boolean;
  reason?: string;
}

export interface AppConfig {
  name: string;
  isSandbox: boolean;
  permissions: {
    balances: AppPermission;
    transactions: AppPermission;
  };
}

export interface InstitutionColors {
  primaryColor?: string | null;
  primaryForeground?: string | null;
  accentColor?: string | null;
  surfaceColor?: string | null;
}

export interface ConnectState {
  currentStep: Steps;
  progress: number;
  applicationId?: string;
  externalUserId?: string;
  appConfig?: AppConfig;
  connectSessionId?: string;
  error?: string;
  accountSelectionMode?: AccountSelectionMode;
  institution?: {
    id: string;
    name: string;
    domain: string;
    iconUrl?: string;
    authFields: FieldSpecification[];
  } & InstitutionColors;
  auth?: {
    needsVerification: boolean;
    isAuthenticated: boolean;
    isVerified: boolean;
  };
  accounts?: {
    selected: AccountDto[];
  };
  connectionId?: string;
  user?: {
    id: string;
    externalUserId: string;
  };
  scopedToken?: string;
  isReconnect?: boolean;
  reconnectConnectionId?: string;
}

export enum ActionType {
  Back,
  InitializeSession,
  InitializeReconnectSession,
  SetInstitution,
  AcceptPermissions,
  SetAuthenticated,
  SetVerified,
  SetSelectedAccounts,
  CompleteReconnect,
  SetError,
  Reset,
}

interface _Action {
  type: ActionType;
  payload?: unknown;
}

export interface InitializeSessionAction extends _Action {
  type: ActionType.InitializeSession;
  payload: {
    applicationId: string;
    externalUserId?: string;
    appConfig: AppConfig;
    sessionId: string;
    accountSelectionMode?: AccountSelectionMode;
  };
}

export interface BackAction extends _Action {
  type: ActionType.Back;
}

export interface SetInstitutionAction extends _Action {
  type: ActionType.SetInstitution;
  payload: {
    id: string;
    name: string;
    domain: string;
    iconUrl?: string;
    authFields: FieldSpecification[];
  } & InstitutionColors;
}

export interface AcceptPermissionsAction extends _Action {
  type: ActionType.AcceptPermissions;
}

export interface SetAuthenticatedAction extends _Action {
  type: ActionType.SetAuthenticated;
  payload: {
    needsVerification: boolean;
  };
}

export interface SetVerifiedAction extends _Action {
  type: ActionType.SetVerified;
  payload: {
    verified: boolean;
  };
}

export interface SetSelectedAccountsAction extends _Action {
  type: ActionType.SetSelectedAccounts;
  payload: {
    connectionId: string;
    accounts: AccountDto[];
    user?: {
      id: string;
      externalUserId: string;
    };
    scopedToken?: string;
  };
}

export interface SetErrorAction extends _Action {
  type: ActionType.SetError;
  payload: {
    error: string;
  };
}

export interface ResetAction extends _Action {
  type: ActionType.Reset;
}

export interface InitializeReconnectSessionAction extends _Action {
  type: ActionType.InitializeReconnectSession;
  payload: {
    sessionId: string;
    connectionId: string;
    appConfig: AppConfig;
    accountSelectionMode?: AccountSelectionMode;
    institution: {
      id: string;
      name: string;
      domain: string;
      iconUrl?: string;
      authFields: FieldSpecification[];
    } & InstitutionColors;
  };
}

export interface CompleteReconnectAction extends _Action {
  type: ActionType.CompleteReconnect;
  payload: {
    connectionId: string;
  };
}

export type Action =
  | InitializeSessionAction
  | InitializeReconnectSessionAction
  | BackAction
  | SetInstitutionAction
  | AcceptPermissionsAction
  | SetAuthenticatedAction
  | SetVerifiedAction
  | SetSelectedAccountsAction
  | CompleteReconnectAction
  | SetErrorAction
  | ResetAction;
